import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useAuthStore } from './auth'
import caseService from '@/services/caseService'
import logger from '@/utils/logger'
import { triggerBlobDownload, extractFilenameFromHeader } from '@/utils/downloadFile'

/**
 * Documents Store for Olvidadme
 * 
 * Implementing Store Pattern from PATTERNS_FROM_BASKET_BASE.md
 * Following ADR-013 (Pinia for state management)
 * Connects with enterprise-ready backend document system
 * 
 * Features:
 * - Document management with field-level encryption
 * - File type classification (DNI, passport, legal docs, etc.)
 * - Secure download with audit trail
 * - GDPR compliance tracking
 * - Document verification status
 * - Advanced filtering and search
 */
export const useDocumentsStore = defineStore('documents', () => {
  // ==========================================
  // STATE - Reactive references
  // ==========================================
  
  const documents = ref([])
  const currentDocument = ref(null)
  const totalDocuments = ref(0)
  const isLoading = ref(false)
  const error = ref(null)
  
  // Filter state
  const filters = ref({
    type: null,
    status: null,
    caseId: null,
    search: null,
    dateFrom: null,
    dateTo: null
  })
  
  // UI state
  const selectedDocumentId = ref(null)
  const showDeleteConfirm = ref(false)
  const downloadProgress = ref(0)
  
  // ==========================================
  // GETTERS - Computed properties
  // ==========================================
  
  const filteredDocuments = computed(() => {
    let filtered = documents.value
    
    if (filters.value.type) {
      filtered = filtered.filter(doc => doc.type === filters.value.type)
    }
    
    if (filters.value.status) {
      filtered = filtered.filter(doc => doc.status === filters.value.status)
    }
    
    if (filters.value.caseId) {
      filtered = filtered.filter(doc => doc.caseId === filters.value.caseId)
    }
    
    if (filters.value.search) {
      const searchTerm = filters.value.search.toLowerCase()
      filtered = filtered.filter(doc => 
        doc.filename.toLowerCase().includes(searchTerm) ||
        doc.originalName?.toLowerCase().includes(searchTerm) ||
        doc.type.toLowerCase().includes(searchTerm)
      )
    }
    
    if (filters.value.dateFrom) {
      filtered = filtered.filter(doc => 
        new Date(doc.createdAt) >= new Date(filters.value.dateFrom)
      )
    }
    
    if (filters.value.dateTo) {
      filtered = filtered.filter(doc => 
        new Date(doc.createdAt) <= new Date(filters.value.dateTo)
      )
    }
    
    return filtered
  })
  
  const documentsByType = computed(() => {
    const grouped = {}
    documents.value.forEach(doc => {
      if (!grouped[doc.type]) {
        grouped[doc.type] = []
      }
      grouped[doc.type].push(doc)
    })
    return grouped
  })
  
  const documentStats = computed(() => {
    const stats = {
      total: documents.value.length,
      verified: 0,
      pending: 0,
      rejected: 0,
      totalSize: 0
    }
    
    documents.value.forEach(doc => {
      stats.totalSize += doc.fileSize || 0
      switch (doc.status) {
        case 'verified':
          stats.verified++
          break
        case 'processed':
        case 'uploading':
          stats.pending++
          break
        case 'rejected':
          stats.rejected++
          break
      }
    })
    
    return stats
  })

  /**
   * Documents filtered by origin - User uploaded documents
   * For Sprint 2: Bidirectional document system
   */
  const userDocuments = computed(() => {
    return documents.value.filter(doc => doc.documentOrigin === 'user')
  })

  /**
   * Documents filtered by origin - Admin/Manager uploaded documents
   * For Sprint 2: Bidirectional document system
   */
  const adminDocuments = computed(() => {
    return documents.value.filter(doc => doc.documentOrigin === 'admin')
  })

  // ==========================================
  // ACTIONS - Store methods
  // ==========================================
  
  /**
   * Fetch all user documents using their caseId
   */
  const fetchDocuments = async (params = {}) => {
    const authStore = useAuthStore()

    if (!authStore.isAuthenticated) {
      logger.warn('Documents fetch attempted without authentication')
      return
    }

    // Get user's caseId from auth store, or fallback to cases store
    let userCaseId = authStore.user?.caseId

    // If caseId not in user profile, try to get from cases store
    if (!userCaseId) {
      try {
        // Import dynamically to avoid circular dependency
        const { useCaseStore } = await import('./cases')
        const casesStore = useCaseStore()

        // Fetch cases if not already loaded
        if (casesStore.cases.length === 0) {
          await casesStore.fetchCases()
        }

        // Get first case ID (1 user = 1 case model)
        if (casesStore.cases.length > 0) {
          userCaseId = casesStore.cases[0]._id || casesStore.cases[0].id
          logger.info('Got caseId from cases store', { caseId: userCaseId })
        }
      } catch (err) {
        logger.warn('Could not get caseId from cases store', { error: err.message })
      }
    }

    if (!userCaseId) {
      logger.info('User has no case yet, no documents to fetch')
      documents.value = []
      totalDocuments.value = 0
      return
    }

    try {
      isLoading.value = true
      error.value = null

      logger.info('Fetching case documents', { caseId: userCaseId, params })

      // Use caseService to get documents for user's case
      const response = await caseService.getCaseDocuments(userCaseId)

      const data = response.data || response
      documents.value = data.documents || data || []
      totalDocuments.value = documents.value.length

      logger.info('Documents fetched successfully', {
        count: documents.value.length
      })

    } catch (err) {
      error.value = err.message
      logger.error('Error fetching documents:', err)
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Upload document to user's case
   * @param {string} caseId - Case ID
   * @param {File} file - File to upload
   * @param {string} documentType - Type of document
   * @param {Function} onProgress - Progress callback
   * @param {string} customLabel - User-provided document description
   */
  const uploadDocument = async (caseId, file, documentType, onProgress = null, customLabel = '') => {
    const authStore = useAuthStore()

    if (!authStore.isAuthenticated) {
      logger.warn('Document upload attempted without authentication')
      throw new Error('Authentication required')
    }

    if (!caseId) {
      throw new Error('Necesitas crear un caso para poder subir documentación')
    }

    try {
      isLoading.value = true
      error.value = null

      logger.info('Uploading document', {
        caseId,
        fileName: file.name,
        documentType
      })

      // Prepare FormData - backend expects 'documents' field
      const formData = new FormData()
      formData.append('documents', file)
      formData.append('type', documentType)
      if (customLabel) {
        formData.append('customLabel', customLabel)
      }
      formData.append('uploadedBy', 'user')

      // Use caseService to upload document
      const response = await caseService.uploadDocument(caseId, formData, onProgress)

      // caseService devuelve response.data (Axios), que es { status, message, data: { uploaded, summary } }
      const responseData = response?.data || response || {}
      const uploadedItems = responseData.uploaded || []
      const firstUploaded = uploadedItems.find(d => !d.error)
      const uploadedDocument = firstUploaded
        ? { ...firstUploaded, _id: firstUploaded._id || firstUploaded.id }
        : null

      // Add to local documents array
      if (uploadedDocument) {
        documents.value.push(uploadedDocument)
        totalDocuments.value = documents.value.length
      }

      logger.info('Document uploaded successfully', {
        documentId: uploadedDocument?._id,
        fileName: file.name
      })

      return uploadedDocument

    } catch (err) {
      error.value = err.message
      logger.error('Error uploading document:', err)
      throw err
    } finally {
      isLoading.value = false
    }
  }
  
  /**
   * Get a single document by ID
   */
  const fetchDocumentById = async (documentId) => {
    const authStore = useAuthStore()
    
    if (!authStore.isAuthenticated) {
      logger.warn('Document fetch attempted without authentication')
      return null
    }
    
    try {
      isLoading.value = true
      error.value = null
      
      logger.info('Fetching document by ID', { documentId })
      
      const response = await fetch(`/api/documents/${documentId}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      })
      
      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Failed to fetch document')
      }
      
      const data = await response.json()
      currentDocument.value = data.data
      
      logger.info('Document fetched successfully', {
        documentId,
        filename: data.data.filename
      })
      
      return data.data
      
    } catch (err) {
      error.value = err.message
      logger.error('Error fetching document:', err)
      return null
    } finally {
      isLoading.value = false
    }
  }
  
  /**
   * Download a document securely
   */
  const downloadDocument = async (documentId) => {
    const authStore = useAuthStore()
    
    if (!authStore.isAuthenticated) {
      logger.warn('Document download attempted without authentication')
      return false
    }
    
    try {
      downloadProgress.value = 0
      error.value = null
      
      logger.info('Starting document download', { documentId })
      
      const response = await fetch(`/api/documents/${documentId}/download`, {
        method: 'GET',
        credentials: 'include'
      })
      
      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Failed to download document')
      }
      
      // Download file
      const filename = extractFilenameFromHeader(response.headers.get('content-disposition'), 'document')
      const blob = await response.blob()
      triggerBlobDownload(blob, filename)
      
      downloadProgress.value = 100
      
      logger.info('Document downloaded successfully', {
        documentId,
        filename
      })
      
      return true
      
    } catch (err) {
      error.value = err.message
      logger.error('Error downloading document:', err)
      return false
    } finally {
      setTimeout(() => {
        downloadProgress.value = 0
      }, 2000)
    }
  }
  
  /**
   * Delete a document with confirmation
   */
  const deleteDocument = async (documentId) => {
    const authStore = useAuthStore()
    
    if (!authStore.isAuthenticated) {
      logger.warn('Document deletion attempted without authentication')
      return false
    }
    
    try {
      isLoading.value = true
      error.value = null
      
      logger.info('Deleting document', { documentId })
      
      const response = await fetch(`/api/documents/${documentId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      })
      
      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Failed to delete document')
      }
      
      // Remove from local state
      const index = documents.value.findIndex(doc => doc._id === documentId)
      if (index > -1) {
        documents.value.splice(index, 1)
        totalDocuments.value = Math.max(0, totalDocuments.value - 1)
      }
      
      // Clear current document if it was the deleted one
      if (currentDocument.value?._id === documentId) {
        currentDocument.value = null
      }
      
      logger.info('Document deleted successfully', { documentId })
      
      return true
      
    } catch (err) {
      error.value = err.message
      logger.error('Error deleting document:', err)
      return false
    } finally {
      isLoading.value = false
    }
  }
  
  /**
   * Set filters for document list
   */
  const setFilters = (newFilters) => {
    filters.value = { ...filters.value, ...newFilters }
    logger.debug('Document filters updated', filters.value)
  }
  
  /**
   * Clear all filters
   */
  const clearFilters = () => {
    filters.value = {
      type: null,
      status: null,
      caseId: null,
      search: null,
      dateFrom: null,
      dateTo: null
    }
    logger.debug('Document filters cleared')
  }
  
  /**
   * Reset store state
   */
  const reset = () => {
    documents.value = []
    currentDocument.value = null
    totalDocuments.value = 0
    error.value = null
    selectedDocumentId.value = null
    showDeleteConfirm.value = false
    downloadProgress.value = 0
    clearFilters()
    
    logger.debug('Documents store reset')
  }
  
  // ==========================================
  // RETURN - Public interface
  // ==========================================
  
  return {
    // State
    documents,
    currentDocument,
    totalDocuments,
    isLoading,
    error,
    filters,
    selectedDocumentId,
    showDeleteConfirm,
    downloadProgress,
    
    // Getters
    filteredDocuments,
    documentsByType,
    documentStats,
    userDocuments,
    adminDocuments,

    // Actions
    fetchDocuments,
    fetchDocumentById,
    uploadDocument,
    downloadDocument,
    deleteDocument,
    setFilters,
    clearFilters,
    reset
  }
})